// src/components/Sidebar/SidebarMenu.jsx
import { useState, useEffect } from 'react';
import {
  List, ListItem, ListItemButton, ListItemText, Collapse, Box,
  Dialog, DialogTitle, DialogContent, Button,
  Card, CardActionArea, Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import LogoutButton from '../Common/LogoutButton';
import { useCategory } from '../../contexts/CategoryContext';
import { useUser } from '../../contexts/UserContext';
import { buildWsUrl } from '../../utils/websocket';
import api from '../../api/axios';

const STORAGE_KEY = 'lastBoardCategory';

export default function SidebarMenu({ onItemClick = () => { } }) {
  const { boardGroups = [], chatCategories = [] } = useCategory();
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const query = location.search;
  const [treeEventActive, setTreeEventActive] = useState(false);
  const [unreadChatIds, setUnreadChatIds] = useState([]);
  const activeChatId = Number(path.match(/\/main\/chat\/category\/(\d+)/)?.[1] || 0);



  useEffect(() => {
    api.get('/api/events/tree/active/')
      .then(r => setTreeEventActive(!!r.data))
      .catch(() => setTreeEventActive(false));
  }, []);


  useEffect(() => {
    if (!userInfo?.logged_in) return;
    api.get('/api/chat/unread_chat_categories/')
      .then((r) => setUnreadChatIds(r.data.unread_category_ids ?? []))
      .catch(console.error);

    const ws = new WebSocket(buildWsUrl('/ws/chat/unread/'));
    ws.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.type === 'chat.unread_update' && Array.isArray(d.unread_category_ids)) {
          setUnreadChatIds(d.unread_category_ids);
        }
      } catch { }
    };
    return () => ws.close();
  }, [userInfo?.logged_in]);

  const [lastCat, setLastCat] = useState(() => localStorage.getItem(STORAGE_KEY));
  useEffect(() => {
    const current = new URLSearchParams(location.search).get('category');
    if (current) {
      setLastCat(current);
      localStorage.setItem(STORAGE_KEY, current);
    }
  }, [location.search]);

  const [openChat, setOpenChat] = useState(path.startsWith('/main/chat'));
  const [openMarket, setOpenMarket] = useState(
    path.startsWith('/main/gacha') || path.startsWith('/main/store'),
  );
  const [openGroup, setOpenGroup] = useState({});
  const toggleGroup = (gid) =>
    setOpenGroup((prev) => ({ ...prev, [gid]: !prev[gid] }));

  const handleBoardClick = (id) => {
    localStorage.setItem(STORAGE_KEY, id);
    setLastCat(String(id));
    navigate(`/main/board?category=${id}`);
    onItemClick();
  };
  const handleChatClick = (id) => {
    setUnreadChatIds((prev) => prev.filter((cid) => cid !== id));
    navigate(`/main/chat/category/${id}`);
    onItemClick();
  };
  const isMatch = (prefix) => path.startsWith(prefix);

  // ✅ 배너 다이얼로그용 상태
  const [dialogOpen, setDialogOpen] = useState(false);
  const openBannerDialog = () => setDialogOpen(true);
  const closeBannerDialog = () => setDialogOpen(false);

  return (
    <>
      <List>
        {/* ===== 메뉴들 ===== */}
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/main/jokbo" onClick={onItemClick}>
            <ListItemText
              primary="📜 족보"
              primaryTypographyProps={{ fontWeight: isMatch('/main/jokbo') ? 700 : 400 }}
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton component={Link} to="/main/calendar" onClick={onItemClick}>
            <ListItemText
              primary="📅 일정"
              primaryTypographyProps={{ fontWeight: isMatch('/main/calendar') ? 700 : 400 }}
            />
          </ListItemButton>
        </ListItem>


        <ListItem disablePadding>
          <ListItemButton component={Link} to="/main/board" onClick={onItemClick}>
            <ListItemText
              primary="📑 전체 게시글"
              primaryTypographyProps={{ fontWeight: path === '/main/board' && !query ? 700 : 400 }}
            />
          </ListItemButton>
        </ListItem>

        {boardGroups.map((g) => (
          <div key={g.id}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => toggleGroup(g.id)}>
                <ListItemText
                  primary={`${g.emoji || '📋'} ${g.name}`}
                  primaryTypographyProps={{ fontWeight: openGroup[g.id] ? 700 : 400 }}
                />
                {openGroup[g.id] ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openGroup[g.id]} timeout="auto" unmountOnExit>
              {g.categories.map((c) => (
                <ListItem key={c.id} disablePadding sx={{ pl: 4 }}>
                  <ListItemButton onClick={() => handleBoardClick(c.id)}>
                    <ListItemText
                      primary={`${c.emoji || '📁'} ${c.name}`}
                      primaryTypographyProps={{
                        fontWeight:
                          query === `?category=${c.id}` || String(c.id) === lastCat ? 700 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </Collapse>
          </div>
        ))}

        {/* ===== 채팅 ===== */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setOpenChat(!openChat)}>
            <ListItemText
              primary="🗣️ 채팅방"
              primaryTypographyProps={{
                fontWeight: openChat ? 700 : 400,
                color:
                  unreadChatIds.some((id) => id !== activeChatId) ? 'error.main' : 'inherit',
                className: unreadChatIds.some((id) => id !== activeChatId) ? 'blink' : '',
              }}
            />
            {openChat ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openChat} timeout="auto" unmountOnExit>
          {chatCategories.map((cat) => {
            const isActive = activeChatId === cat.id;
            const shouldBlink = unreadChatIds.includes(cat.id) && !isActive;
            return (
              <ListItem key={cat.id} disablePadding sx={{ pl: 4 }}>
                <ListItemButton
                  onClick={() => handleChatClick(cat.id)}
                  className={shouldBlink ? 'blink' : ''}
                >
                  <ListItemText
                    primary={`${cat.emoji || '💬'} ${cat.name}`}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 700 : 400,
                      color: shouldBlink ? 'error.main' : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </Collapse>

        {/* ===== 마켓 ===== */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setOpenMarket(!openMarket)}>
            <ListItemText
              primary="🏪 산들마켓"
              primaryTypographyProps={{ fontWeight: openMarket ? 700 : 400 }}
            />
            {openMarket ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openMarket} timeout="auto" unmountOnExit>
          <ListItem disablePadding sx={{ pl: 4 }}>
            <ListItemButton component={Link} to="/main/gacha/title" onClick={onItemClick}>
              <ListItemText
                primary="🎖️ 칭호뽑기"
                primaryTypographyProps={{ fontWeight: isMatch('/main/gacha/title') ? 700 : 400 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ pl: 4 }}>
            <ListItemButton component={Link} to="/main/gacha/luckybox" onClick={onItemClick}>
              <ListItemText
                primary="🧧 칭호 럭키박스"
                primaryTypographyProps={{ fontWeight: isMatch('/main/gacha/luckybox') ? 700 : 400 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ pl: 4 }}>
            <ListItemButton component={Link} to="/main/store" onClick={onItemClick}>
              <ListItemText
                primary="🛒 아이템 상점"
                primaryTypographyProps={{ fontWeight: isMatch('/main/store') ? 700 : 400 }}
              />
            </ListItemButton>
          </ListItem>
        </Collapse>

        {/* 로그아웃 버튼 */}
        <LogoutButton />

        {/* 운영정책 링크 */}
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href="https://glossy-thief-ee9.notion.site/2354d005ad0780a6aceee0bd2e9d3e50?source=copy_link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onItemClick}
            aria-label="운영정책(새 창)"
          >
            <ListItemText primary="📄 운영정책" />
          </ListItemButton>
        </ListItem>

        {/* ✅ 이벤트 배너 (트리 꾸미기) - 운영정책 아래, 기존 배너 위 */}
        {treeEventActive && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 10px 24px rgba(0,0,0,0.10)',
              }}
            >
              <CardActionArea
                onClick={() => {
                  navigate('/main/events/tree');
                  onItemClick();
                }}
                sx={{
                  position: 'relative',
                  p: 2,
                  // ✅ 크리스마스 무드: 은은한 레드/그린 + 화이트
                  background:
                    'linear-gradient(135deg, rgba(220,20,60,0.14), rgba(255,255,255,0.92) 45%, rgba(0,160,80,0.14))',
                }}
              >
                {/* ✅ 살짝 반짝(너무 과하지 않게) */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    opacity: 0.9,
                    background: `
              radial-gradient(circle at 18% 25%, rgba(255,255,255,0.55), transparent 40%),
              radial-gradient(circle at 80% 30%, rgba(255,255,255,0.45), transparent 42%),
              radial-gradient(circle at 55% 85%, rgba(255,255,255,0.35), transparent 45%)
            `,
                  }}
                />

                <Typography
                  sx={{
                    position: 'relative',
                    fontWeight: 900,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1,
                  }}
                >
                  트리 꾸미기 이벤트
                </Typography>



                {/* ✅ 오른쪽 화살표/아이콘 없이도 클릭 유도되게 하단 라인만 살짝 */}
                <Box
                  sx={{
                    position: 'relative',
                    mt: 1.4,
                    height: 2,
                    width: 44,
                    borderRadius: 999,
                    backgroundColor: 'rgba(0,0,0,0.12)',
                  }}
                />
              </CardActionArea>
            </Card>
          </Box>
        )}

        {/* ✅ 배너 이미지 클릭 → 다이얼로그 띄움 */}
        <Box sx={{ p: 2 }}>
          <img
            src="/images/banner1.png"
            alt="산들마켓 이벤트 배너"
            style={{
              width: '100%',
              borderRadius: 8,
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              cursor: 'pointer',
            }}
            onClick={openBannerDialog}
          />
        </Box>
      </List>

      {/* ✅ 배너 다이얼로그 */}
      <Dialog open={dialogOpen} onClose={closeBannerDialog}>
        <DialogTitle>어디로 이동할까요?</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => window.open('https://xn--6e0bkp381dujj.com/', '_blank')}
          >
            🌐 사이트로 이동
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={() => window.open('https://open.kakao.com/o/sj4Z0QIh', '_blank')}
          >
            💬 카카오톡으로 문의하기
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
